"use client";

import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonLoading,
  useIonViewWillEnter,
  IonButton,
  IonDatetime,
  IonDatetimeButton,
  IonModal,
  IonLabel,
  IonToast
} from "@ionic/react";
import {
  TreeView,
  TreeItem,
  treeItemClasses
} from "@mui/lab";
import {
  TextField,
  Typography,
  Paper,
  Box,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  alpha,
  LinearProgress,
  Tooltip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FolderIcon from '@mui/icons-material/Folder';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axios from "axios";
import { requestID, baseUrl, config } from "../../../../common/utils";
import { useBusinessContext } from "../../../../context/business-context";

// Interfaces
interface AccountNode {
  coa_id: string;
  account_code: string;
  account_name: string;
  compID: string;
  credit: number | string;
  debit: number | string;
  balance: number;
  is_postable: number;
  ledgerName: string;
  level: number;
  parent_id: string | null;
  children: AccountNode[];
}

interface FinancialReportResponse {
  accounts: AccountNode[];
  totals: {
    debit: number;
    credit: number;
  };
}

interface TreeNode {
  id: string;
  name: string;
  type: 'ledger-group' | 'account';
  level: number;
  debit: number;
  credit: number;
  balance: number;
  children?: TreeNode[];
  accountCode?: string;
  ledgerName?: string;
  originalData?: AccountNode;
  parentId?: string | null;
}

// Minimal Styled Components
const SimpleTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    borderRadius: 4,
    margin: '2px 0',
    padding: '8px 12px',
    border: '1px solid #e5e7eb',
    '&:hover': {
      backgroundColor: '#f8fafc',
    },
  },
  [`& .${treeItemClasses.group}`]: {
    marginLeft: 20,
    borderLeft: '1px solid #d1d5db',
    paddingLeft: 8,
  },
}));

const SimpleButton = styled(Button)({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 500,
});

// Helper functions
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const parseAmount = (amount: number | string): number => {
  if (typeof amount === 'string') {
    return parseFloat(amount.replace(/,/g, '')) || 0;
  }
  return amount || 0;
};

const buildFinancialReportTreeGroupedByLedger = (accounts: AccountNode[]): TreeNode[] => {
  if (!accounts || accounts.length === 0) return [];

  const accountsByLedger: Record<string, AccountNode[]> = {};
  const allAccounts: AccountNode[] = [];

  const collectAccounts = (account: AccountNode) => {
    allAccounts.push(account);
    account.children?.forEach(collectAccounts);
  };

  accounts.forEach(collectAccounts);

  allAccounts.forEach(account => {
    const ledgerName = account.ledgerName || 'Other';
    if (!accountsByLedger[ledgerName]) {
      accountsByLedger[ledgerName] = [];
    }
    accountsByLedger[ledgerName].push(account);
  });

  const buildHierarchy = (ledgerAccounts: AccountNode[]): TreeNode[] => {
    const nodeMap = new Map<string, TreeNode>();
    const roots: TreeNode[] = [];

    ledgerAccounts.forEach(account => {
      const node: TreeNode = {
        id: account.coa_id,
        name: account.account_name,
        type: 'account',
        level: account.level,
        debit: parseAmount(account.debit),
        credit: parseAmount(account.credit),
        balance: account.balance || (parseAmount(account.debit) - parseAmount(account.credit)),
        accountCode: account.account_code,
        ledgerName: account.ledgerName,
        originalData: account,
        parentId: account.parent_id,
      };
      nodeMap.set(account.coa_id, node);
    });

    nodeMap.forEach(node => {
      if (node.parentId && nodeMap.has(node.parentId)) {
        const parent = nodeMap.get(node.parentId)!;
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots.sort((a, b) => (a.accountCode || '').localeCompare(b.accountCode || ''));
  };

  const tree: TreeNode[] = [];

  Object.entries(accountsByLedger).forEach(([ledgerName, ledgerAccounts]) => {
    const hierarchy = buildHierarchy(ledgerAccounts);
    
    let ledgerDebit = 0;
    let ledgerCredit = 0;
    let ledgerBalance = 0;

    const calculateTotals = (nodes: TreeNode[]) => {
      nodes.forEach(node => {
        ledgerDebit += node.debit;
        ledgerCredit += node.credit;
        ledgerBalance += node.balance;
        if (node.children) calculateTotals(node.children);
      });
    };

    calculateTotals(hierarchy);

    tree.push({
      id: `ledger-${ledgerName}`,
      name: ledgerName,
      type: 'ledger-group',
      level: 0,
      debit: ledgerDebit,
      credit: ledgerCredit,
      balance: ledgerBalance,
      children: hierarchy,
      ledgerName
    });
  });

  return tree.sort((a, b) => a.name.localeCompare(b.name));
};

const FinancialReportTree: React.FC = () => {
  const { selectedBusinessId, selectedBusinessName } = useBusinessContext();

  // State
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [filteredTreeData, setFilteredTreeData] = useState<TreeNode[]>([]);
  const [reportTypes, setReportTypes] = useState<any[]>([]);
  const [selectedReportType, setSelectedReportType] = useState<string>("");
  const [endDate, setEndDate] = useState<string>(new Date().toISOString());
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState({ loading: false, error: false });
  const [errorMessage, setErrorMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0, totalBalance: 0 });

  // Get report types
  const getReportTypes = async () => {
    try {
      setStatus({ loading: true, error: false });
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusinessId);

      const response = await axios.post(
        baseUrl() + "ledgers/getMothers",
        formData,
        config
      );

      setReportTypes(response.data.data || []);
      setStatus({ loading: false, error: false });
    } catch (error) {
      setStatus({ loading: false, error: true });
      setErrorMessage("Failed to load report types");
      setShowToast(true);
    }
  };

  // Generate report
  const generateFinancialReport = async () => {
    if (!selectedReportType) {
      setErrorMessage("Please select a report type");
      setShowToast(true);
      return;
    }

    try {
      setStatus({ loading: true, error: false });
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("business_id", selectedBusinessId);
      formData.append("endDate", endDate.split('T')[0]);
      formData.append("reportType", selectedReportType);

      const response = await axios.post(
        baseUrl() + "Geacloud_FinancialReports/index",
        formData,
        config
      );

      const reportData: FinancialReportResponse = response.data.data || { accounts: [], totals: { debit: 0, credit: 0 } };
      const tree = buildFinancialReportTreeGroupedByLedger(reportData.accounts || []);
      setTreeData(tree);
      setFilteredTreeData(tree);

      // Expand ledger groups by default
      const ledgerGroupIds = tree.map(node => node.id);
      setExpandedNodes(ledgerGroupIds);

      // Calculate totals
      const totalDebit = parseAmount(reportData.totals?.debit || 0);
      const totalCredit = parseAmount(reportData.totals?.credit || 0);
      setTotals({
        totalDebit,
        totalCredit,
        totalBalance: totalDebit - totalCredit
      });

      setStatus({ loading: false, error: false });
    } catch (error) {
      setStatus({ loading: false, error: true });
      setErrorMessage("Failed to generate report");
      setShowToast(true);
    }
  };

  // Handle search
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredTreeData(treeData);
      return;
    }

    const lowercasedTerm = term.toLowerCase();
    const filterNodes = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.filter(node => {
        const matches = 
          node.name.toLowerCase().includes(lowercasedTerm) ||
          (node.accountCode && node.accountCode.toLowerCase().includes(lowercasedTerm)) ||
          (node.ledgerName && node.ledgerName.toLowerCase().includes(lowercasedTerm));

        if (node.children) {
          const filteredChildren = filterNodes(node.children);
          if (filteredChildren.length > 0) return true;
        }

        return matches;
      });
    };

    setFilteredTreeData(filterNodes(treeData));
  };

  // Render tree node
  const renderTreeNode = (node: TreeNode): JSX.Element => {
    const isExpanded = expandedNodes.includes(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <SimpleTreeItem
        key={node.id}
        nodeId={node.id}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {node.type === 'ledger-group' ? (
                <FolderIcon sx={{ color: '#3b82f6', fontSize: 18 }} />
              ) : hasChildren ? (
                <FolderIcon sx={{ color: '#8b5cf6', fontSize: 16 }} />
              ) : (
                <AccountTreeIcon sx={{ color: '#94a3b8', fontSize: 16 }} />
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {node.type === 'account' && node.accountCode && (
                  <Typography variant="caption" sx={{ color: '#64748b', fontFamily: 'monospace', minWidth: 80 }}>
                    {node.accountCode}
                  </Typography>
                )}
                
                <Typography
                  variant={node.type === 'ledger-group' ? "subtitle2" : "body2"}
                  sx={{
                    fontWeight: node.type === 'ledger-group' ? 600 : hasChildren ? 500 : 400,
                    color: node.type === 'ledger-group' ? '#1e293b' : '#334155'
                  }}
                >
                  {node.name}
                </Typography>
                
                {node.type === 'account' && (
                  <Chip
                    label={`L${node.level}`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      backgroundColor: '#f1f5f9',
                      color: '#64748b'
                    }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: node.debit > 0 ? '#059669' : '#64748b',
                  minWidth: 100,
                  textAlign: 'right',
                  fontFamily: 'monospace'
                }}
              >
                {formatCurrency(node.debit)}
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: node.credit > 0 ? '#dc2626' : '#64748b',
                  minWidth: 100,
                  textAlign: 'right',
                  fontFamily: 'monospace'
                }}
              >
                {formatCurrency(node.credit)}
              </Typography>
              
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: node.balance > 0 ? '#059669' : node.balance < 0 ? '#dc2626' : '#64748b',
                  minWidth: 100,
                  textAlign: 'right',
                  fontFamily: 'monospace'
                }}
              >
                {formatCurrency(node.balance)}
              </Typography>
            </Box>
          </Box>
        }
        onClick={() => {
          if (hasChildren) {
            setExpandedNodes(prev =>
              isExpanded
                ? prev.filter(id => id !== node.id)
                : [...prev, node.id]
            );
          }
        }}
      >
        {node.children?.map(child => renderTreeNode(child))}
      </SimpleTreeItem>
    );
  };

  // Initialize
  useIonViewWillEnter(() => {
    getReportTypes();
  }, []);

  return (
    <IonPage className="pageDown1">
      <IonHeader>
        <IonToolbar>
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b" }}>
              Financial Reports
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedBusinessName || 'Business'}
            </Typography>
          </Box>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 2 }}>
          {/* Controls */}
          <Card sx={{ mb: 2, borderRadius: 1 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Report Type</InputLabel>
                    <Select
                      value={selectedReportType}
                      onChange={(e) => setSelectedReportType(e.target.value)}
                      label="Report Type"
                    >
                      <MenuItem value="">Select report type</MenuItem>
                      {reportTypes.slice(0,1).map((type, index) => (
                        <MenuItem key={index} value={type.motherID}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IonLabel style={{ fontSize: '0.875rem' }}>Date:</IonLabel>
                    <IonDatetimeButton datetime="reportDate" />
                    <IonModal keepContentsMounted={true}>
                      <IonDatetime
                        id="reportDate"
                        value={endDate}
                        onIonChange={(e) => setEndDate(e.detail.value!)}
                        presentation="date"
                        showDefaultButtons
                      />
                    </IonModal>
                  </Box>
                </Grid>

                <Grid item xs={12} md={2}>
                  <SimpleButton
                    fullWidth
                    variant="contained"
                    onClick={generateFinancialReport}
                    disabled={!selectedReportType || status.loading}
                    startIcon={<RefreshIcon />}
                  >
                    Generate
                  </SimpleButton>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchTerm("")}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              {status.loading && (
                <LinearProgress sx={{ mt: 2 }} />
              )}
            </CardContent>
          </Card>

          {/* Totals Bar */}
          {filteredTreeData.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #dcfce7' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <TrendingUpIcon sx={{ color: '#059669', fontSize: 16 }} />
                  <Typography variant="caption" color="#64748b">
                    Total Debit
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669' }}>
                  {formatCurrency(totals.totalDebit)}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fee2e2' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <TrendingDownIcon sx={{ color: '#dc2626', fontSize: 16 }} />
                  <Typography variant="caption" color="#64748b">
                    Total Credit
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626' }}>
                  {formatCurrency(totals.totalCredit)}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, bgcolor: '#eff6ff', borderRadius: 1, border: '1px solid #dbeafe' }}>
                <Typography variant="caption" color="#64748b" sx={{ mb: 0.5 }}>
                  Net Balance
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: totals.totalBalance >= 0 ? '#059669' : '#dc2626' 
                  }}
                >
                  {formatCurrency(totals.totalBalance)}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Tree View */}
          {filteredTreeData.length > 0 ? (
            <Paper sx={{ borderRadius: 1, border: '1px solid #e5e7eb' }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr auto auto auto',
                gap: 2,
                p: 2,
                borderBottom: '1px solid #e5e7eb',
                bgcolor: '#f8fafc'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
                  Account
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#059669', textAlign: 'right' }}>
                  Debit
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#dc2626', textAlign: 'right' }}>
                  Credit
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#3b82f6', textAlign: 'right' }}>
                  Balance
                </Typography>
              </Box>

              <Box sx={{ maxHeight: '500px', overflow: 'auto', p: 1 }}>
                <TreeView
                  expanded={expandedNodes}
                  onNodeToggle={(_, nodeIds) => setExpandedNodes(nodeIds)}
                  defaultCollapseIcon={<RemoveIcon />}
                  defaultExpandIcon={<AddIcon />}
                >
                  {filteredTreeData.map((node) => renderTreeNode(node))}
                </TreeView>
              </Box>
            </Paper>
          ) : searchTerm ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SearchIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try a different search term
              </Typography>
              <SimpleButton variant="outlined" onClick={() => setSearchTerm("")}>
                Clear Search
              </SimpleButton>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AssessmentIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                Generate a report
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a report type and click generate
              </Typography>
            </Box>
          )}
        </Box>

        <IonLoading
          message="Loading..."
          spinner="circles"
          isOpen={status.loading}
        />
        
        <IonToast
          isOpen={showToast}
          message={errorMessage}
          duration={3000}
          onDidDismiss={() => setShowToast(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default FinancialReportTree;